import { supabase } from './supabase';
import type {
  AuditRow, CartLine, ClaimResult, CommitteeLive, CommitteeNotice, Dashboard, LiveBoard, NoticeTone, NoticeTopic, Venue, EmailStats, ExportOrder, FamilyMember, FlatDetail, FlatInfo, FlatRow,
  FoundPass, Invite, InviteStatus, Me, MenuItem, Order, OrderStatus, Pass, Person, PreviewResult, PublicPasses, QueueOrder, Role,
  SeatInfo, ServeExport, Settings, Sitting, Stats, Kind, Via, AccountKind, GatewayInfo, PaymentAccount, PayQuote, StatementResult,
  StatementRow, StatementView, PortalEvent, AdminEvent, EventStatus, Meal, BhogUse, FamilyInbox, FlatCheck, JoinRequest, KitchenCount,
  PastEvents, MyEvent, MyOrder, Receipts, Org, EventPage, ProgrammeEvent, ProgrammeItem, EventContact, EventNote,
} from './types';

export type CatalogScopeName = 'current' | 'all';
export type ReceiptScope = { code: string; eventId?: never } | { eventId: string; code?: never };

export class ApiError extends Error {
  constructor(message: string, readonly details?: string | null, readonly code?: string) {
    super(message);
  }
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new ApiError(error.message, error.details, error.code);
  return data as T;
}

/** The pay Edge Function (Cashfree). Errors come back as {error: code} with a non-2xx status. */
async function payFn<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('pay', { body });
  if (error) {
    let code = 'gateway_unreachable';
    try {
      const ctx = (error as { context?: Response }).context;
      const j = ctx ? await ctx.json() as { error?: string } : null;
      if (j?.error) code = j.error;
    } catch { /* not JSON */ }
    throw new ApiError(code);
  }
  return data as T;
}

const DEFAULT_SETTINGS: Settings = {
  booking_open: true,
  bhog_cap_per_flat: 10,
  undo_window_seconds: 120,
  serve_early_minutes: 30,
  enforce_serve_window: true,
  many_orders_warning: 3,
  upi_include_amount: false,
  email_notifications: true,
  email_codes_per_hour: 5,
  email_daily_cap: 290,
  push_notifications: true,
  last_call_minutes: 20,
  online_payments: false,
  payments_test_mode: false,
  online_fee_percent: 0,
  online_fee_free_upto: 0,
  online_fee_free_until: null,
  resident_cash: true,
  cash_two_person: true,
};

export type ReviewDecision = 'confirm' | 'send_back' | 'spam' | 'to_check' | 'cancel';
export type ServeLine = { kind: Kind; adults: number; children: number };
export type FlatFilter = 'all' | 'no_email' | 'open' | 'shared';
export type ServePreview = {
  result: PreviewResult;
  via?: Via;
  pass?: Pass;
  seat?: SeatInfo | null;
  /** A family pass at a meal the flat didn't book. */
  flat?: { label: string; name: string };
};

export const api = {
  me: () => rpc<Me>('me'),
  saveMember: (name: string, phone: string) => rpc<Me>('save_member', { p_name: name, p_phone: phone }),
  setNotify: (on: boolean) => rpc<Me>('set_notify', { p_on: on }),
  linkNewFlat: (name: string, phone: string, tower: number, flat: string) =>
    rpc<Me>('link_new_flat', { p_name: name, p_phone: phone, p_tower: tower, p_flat: flat }),
  claimComplete: (token: string) => rpc<Me>('claim_complete', { p_token: token }),
  claimPeek: (token: string) => rpc<ClaimResult>('claim_peek', { p_token: token }),
  committeeInvite: (flatId: string) => rpc<Invite>('committee_invite', { p_flat_id: flatId }),
  committeeInvites: (flatIds?: string[]) => rpc<Record<string, InviteStatus>>('committee_invites', { p_flat_ids: flatIds ?? null }),
  /** Whether the flat of a scanned family pass has an email yet. */
  counterInviteCheck: (code: string) => rpc<{ flat: string; emails: number }>('counter_invite', { p_code: code, p_create: false }),
  counterInvite: (code: string) => rpc<Invite>('counter_invite', { p_code: code, p_create: true }),
  flatCheck: (tower: number, flat: string) => rpc<FlatCheck>('flat_check', { p_tower: tower, p_flat: flat }),
  joinRequest: (name: string, phone: string, tower: number, flat: string) =>
    rpc<Me>('join_request', { p_name: name, p_phone: phone, p_tower: tower, p_flat: flat }),
  cancelRequest: () => rpc<Me>('cancel_request'),
  familyInbox: () => rpc<FamilyInbox>('family_inbox'),
  answerRequest: (id: string, approve: boolean) => rpc<FamilyInbox>('answer_request', { p_id: id, p_approve: approve }),
  mergeRequest: () => rpc<FamilyInbox>('merge_request'),
  familyLeave: () => rpc<Me>('family_leave'),
  familySetFlat: (tower: number, flat: string) => rpc<Me>('family_set_flat', { p_tower: tower, p_flat: flat }),
  familyInvite: () => rpc<Invite>('family_invite'),
  bhogUsed: (eventId: string, flatId?: string) => rpc<BhogUse>('bhog_used', { p_event_id: eventId, p_flat_id: flatId ?? null }),
  family: () => rpc<FamilyMember[]>('my_family'),
  familyAdd: (email: string, name: string) => rpc<FamilyMember[]>('family_add', { p_email: email, p_name: name }),
  familyRemove: (email: string) => rpc<FamilyMember[]>('family_remove', { p_email: email }),
  publicPasses: (token: string) => rpc<PublicPasses | null>('public_passes', { p_token: token }),
  /** Keep a seat for a guest, or take it back. `token` is the passes link token when not signed in. */
  giveSeat: (seatId: string, on: boolean, token?: string) =>
    rpc<Pass>('give_seat', { p_seat_id: seatId, p_on: on, p_token: token ?? null }),
  reissueMine: () => rpc<Dashboard>('reissue_my_codes'),
  pushSubscribe: (endpoint: string, p256dh: string, auth: string) =>
    rpc<{ on: boolean }>('push_subscribe', { p_endpoint: endpoint, p_p256dh: p256dh, p_auth: auth, p_user_agent: navigator.userAgent }),
  pushUnsubscribe: (endpoint: string) => rpc<{ on: boolean }>('push_unsubscribe', { p_endpoint: endpoint }),
  pushTest: (endpoint: string) => rpc<number>('push_test', { p_endpoint: endpoint }),

  /**
   * Meals and menus. 'current' is the events that are on (and drafts, for admins): what residents book, scan and see
   * live. Past events' meals come with their passes and orders, so this list doesn't grow with every event. Staff
   * screens look back at past events and ask for 'all'.
   */
  sittings: async (scope: CatalogScopeName = 'current'): Promise<Sitting[]> => {
    let q = supabase.from('sittings').select(scope === 'all' ? '*' : '*, events!inner()');
    if (scope !== 'all') q = q.not('events.status', 'in', '(closed,archived)');
    const { data, error } = await q.order('opens_at').order('sort');
    if (error) throw new ApiError(error.message);
    return data as unknown as Sitting[];
  },
  menu: async (scope: CatalogScopeName = 'current'): Promise<MenuItem[]> => {
    let q = supabase.from('menu_items').select(scope === 'all' ? '*' : '*, sittings!inner(events!inner())');
    if (scope !== 'all') q = q.not('sittings.events.status', 'in', '(closed,archived)');
    const { data, error } = await q;
    if (error) throw new ApiError(error.message);
    return data as unknown as MenuItem[];
  },
  /** An event's public page: days, description, programme, meals, contacts and notes. */
  eventPage: (eventId: string) => rpc<EventPage>('event_page', { p_id: eventId }),
  programmeEvents: () => rpc<ProgrammeEvent[]>('programme_events'),
  saveProgrammeItem: (id: string | null, eventId: string, item: Pick<ProgrammeItem, 'topic' | 'title' | 'title_bn' | 'starts_at' | 'ends_at' | 'venue_id' | 'note' | 'remind'>) =>
    rpc<ProgrammeItem>('save_programme_item', {
      p_id: id, p_event_id: eventId, p_topic: item.topic, p_title: item.title, p_title_bn: item.title_bn, p_starts_at: item.starts_at,
      p_ends_at: item.ends_at, p_venue_id: item.venue_id, p_note: item.note, p_remind: item.remind,
    }),
  deleteProgrammeItem: (id: string) => rpc<unknown>('delete_programme_item', { p_id: id }),
  saveEventPage: (eventId: string, about: string, contacts: EventContact[], notes: EventNote[]) =>
    rpc<EventPage>('save_event_page', { p_id: eventId, p_about: about, p_contacts: contacts, p_notes: notes }),
  setEventDates: (eventId: string, startsOn: string, endsOn: string) =>
    rpc<AdminEvent>('admin_set_event_dates', { p_id: eventId, p_starts_on: startsOn, p_ends_on: endsOn }),
  /** The society's details (one society for now). */
  org: async (): Promise<Org | null> => {
    const { data, error } = await supabase.from('organisations').select('id, code, name, payee_name, address, contact_email')
      .order('created_at').limit(1).maybeSingle();
    if (error) throw new ApiError(error.message);
    return data as Org | null;
  },
  saveOrg: (name: string, payeeName: string, address: string, contactEmail: string) =>
    rpc<Org>('admin_save_org', { p_name: name, p_payee_name: payeeName, p_address: address, p_contact_email: contactEmail }),
  pastEvents: (limit: number, before?: string | null) => rpc<PastEvents>('my_past_events', { p_limit: limit, p_before: before ?? null }),
  myEvent: (eventId: string) => rpc<MyEvent>('my_event', { p_event_id: eventId }),
  myOrder: (code: string) => rpc<MyOrder>('my_order', { p_code: code }),
  /** Receipt data for one confirmed order, or for every confirmed order of an event. */
  myReceipts: (scope: ReceiptScope) => rpc<Receipts>('my_receipts', { p_event_id: scope.eventId ?? null, p_code: scope.code ?? null }),
  committeeReceipts: (flatId: string, scope: ReceiptScope) =>
    rpc<Receipts>('committee_receipts', { p_flat_id: flatId, p_event_id: scope.eventId ?? null, p_code: scope.code ?? null }),
  /** Live and closed events (and drafts, for admins), oldest first. */
  events: async (): Promise<PortalEvent[]> => {
    const { data, error } = await supabase.from('events')
      .select('id, org_id, name, name_bn, code, status, booking_open, bhog_cap_per_flat, starts_on, ends_on, created_at').order('created_at');
    if (error) throw new ApiError(error.message);
    return data as PortalEvent[];
  },
  venues: async (): Promise<Venue[]> => {
    const { data, error } = await supabase.from('venues').select('id, name, name_bn, where_text, notes, sort').order('sort');
    if (error) throw new ApiError(error.message);
    return data as Venue[];
  },
  liveBoard: () => rpc<LiveBoard>('live_board'),
  settings: async (): Promise<Settings> => {
    const { data, error } = await supabase.from('settings').select('key, value');
    if (error) throw new ApiError(error.message);
    const out: Record<string, unknown> = { ...DEFAULT_SETTINGS };
    for (const row of data ?? []) out[row.key as string] = row.value;
    return out as unknown as Settings;
  },

  placeOrder: (lines: CartLine[]) => rpc<Order>('place_order', { p_lines: lines }),
  submitUtr: (code: string, utr: string) => rpc<Order>('submit_utr', { p_code: code, p_utr: utr }),
  cancelMyOrder: (code: string) => rpc<Order>('cancel_my_order', { p_code: code }),
  reportPaymentIssue: (code: string, note: string) => rpc<Order>('report_payment_issue', { p_code: code, p_note: note }),
  clearPaymentIssue: (code: string) => rpc<Order>('committee_clear_payment_issue', { p_code: code }),
  payQuote: (code: string) => rpc<PayQuote>('pay_quote', { p_code: code }),
  choosePayMode: (code: string, mode: 'upi' | 'cash') => rpc<Order>('choose_pay_mode', { p_code: code, p_mode: mode }),
  /** A Cashfree payment session for the order (the same one again while it is open). */
  payStart: (code: string, phone?: string) =>
    payFn<{ session_id: string; mode: 'sandbox' | 'production'; amount: number; fee: number; ref: string }>({ action: 'start', code, phone: phone ?? null }),
  /** Ask Cashfree about the order's payments (after coming back from checkout, or from the committee console). */
  payCheck: (code: string) => payFn<{ status: OrderStatus; confirmed: boolean; pending?: boolean }>({ action: 'check', code }),
  dashboard: () => rpc<Dashboard>('my_dashboard'),

  /** One event's orders; a search looks across every event. */
  queue: (status: OrderStatus | 'all' | 'cash_due' | 'paid_twice' | 'reported', q: string, eventId: string | null) =>
    rpc<QueueOrder[]>('committee_queue', { p_status: status, p_q: q.trim() || null, p_event_id: eventId }),
  stats: (eventId: string | null) => rpc<Stats>('committee_stats', { p_event_id: eventId }),
  review: (code: string, decision: ReviewDecision, reason?: string, account?: string) =>
    rpc<QueueOrder>('review_order', { p_code: code, p_decision: decision, p_reason: reason ?? null, p_account: account ?? null }),
  recordCash: (code: string, account: string, note?: string) =>
    rpc<Order>('committee_record_cash', { p_code: code, p_account: account, p_note: note ?? null }),
  /** Totals for one event, or every event without one. */
  accounts: (eventId?: string | null) => rpc<PaymentAccount[]>('committee_accounts', { p_event_id: eventId ?? null }),
  saveAccount: (id: string | null, name: string, kind: Exclude<AccountKind, 'online'>, details: string, active: boolean) =>
    rpc<PaymentAccount[]>('admin_save_account', { p_id: id, p_name: name, p_kind: kind, p_details: details, p_active: active }),
  statement: (filter: 'open' | 'matched' | 'ignored', account?: string) =>
    rpc<StatementView>('statement_view', { p_filter: filter, p_account: account ?? null }),
  uploadStatement: (account: string, file: string, rows: StatementRow[]) =>
    rpc<StatementResult>('statement_upload', { p_account: account, p_file: file, p_rows: rows }),
  linkStatement: (lineId: number, code: string, force = false) =>
    rpc<StatementView>('statement_link', { p_line: lineId, p_code: code, p_force: force }),
  unlinkStatement: (lineId: number) => rpc<StatementView>('statement_unlink', { p_line: lineId }),
  ignoreStatement: (lineId: number, on: boolean) => rpc<StatementView>('statement_ignore', { p_line: lineId, p_on: on }),
  gateway: () => rpc<GatewayInfo>('admin_gateway'),
  saveGateway: (mode: 'sandbox' | 'production', clientId: string, secret: string, enabled: boolean) =>
    rpc<GatewayInfo>('admin_save_gateway', { p_mode: mode, p_client_id: clientId, p_secret: secret, p_enabled: enabled }),
  testGateway: () => payFn<{ ok: boolean; status: number; mode: string; message: string | null }>({ action: 'test' }),
  kitchen: (eventId: string) => rpc<KitchenCount>('kitchen_counts', { p_event_id: eventId }),
  backupList: (sittingId: string) => rpc<{ sitting: Sitting; rows: Pass[] }>('backup_list', { p_sitting_id: sittingId }),

  flats: (q: string, filter: FlatFilter) => rpc<FlatRow[]>('committee_flats', { p_q: q.trim() || null, p_filter: filter }),
  flat: (id: string) => rpc<FlatDetail>('committee_flat', { p_flat_id: id }),
  /** another: add a second family at a flat that already has one. bhog_over: events where the flat is now over the Bhog limit. */
  saveFlat: (id: string | null, tower: number, flat: string, contactName: string, contactPhone: string, another = false) =>
    rpc<FlatInfo & { bhog_over: { event: string; cap: number; booked: number }[] }>('committee_save_flat', {
      p_id: id, p_tower: tower, p_flat: flat, p_contact_name: contactName, p_contact_phone: contactPhone, p_another: another,
    }),
  joinRequests: () => rpc<JoinRequest[]>('committee_join_requests'),
  answerJoinRequest: (id: string, flatId: string | null, approve: boolean) =>
    rpc<JoinRequest[]>('committee_answer_request', { p_id: id, p_flat_id: flatId, p_approve: approve }),
  mergeFamilies: (keepId: string, dropId: string) => rpc<FlatDetail>('committee_merge', { p_keep: keepId, p_drop: dropId }),
  setMovedOut: (flatId: string, on: boolean) => rpc<FlatDetail>('committee_set_moved_out', { p_flat_id: flatId, p_on: on }),
  addEmail: (flatId: string, email: string, name: string, move = false) =>
    rpc<FlatDetail>('committee_add_email', { p_flat_id: flatId, p_email: email, p_name: name, p_move: move }),
  removeEmail: (email: string) => rpc<FlatDetail>('committee_remove_email', { p_email: email }),
  sendPasses: (flatId: string, email?: string) => rpc<number>('committee_send_passes', { p_flat_id: flatId, p_email: email ?? null }),
  reissueFlat: (flatId: string) => rpc<FlatDetail>('committee_reissue_flat', { p_flat_id: flatId }),
  committeeBook: (flatId: string, lines: CartLine[], payMode: 'upi' | 'cash', utr: string, note: string, account: string | null) =>
    rpc<Order>('committee_book', { p_flat_id: flatId, p_lines: lines, p_pay_mode: payMode, p_utr: utr, p_note: note, p_account: account }),
  moveOrder: (code: string, flatId: string) => rpc<Order>('committee_move_order', { p_code: code, p_flat_id: flatId }),
  emailStats: () => rpc<EmailStats>('email_stats'),
  committeeLive: () => rpc<CommitteeLive>('committee_live'),
  /** minutes null: until the meal is over, an hour after the start time, or 2 hours. remind: an alert 15 minutes before it starts. */
  announce: (n: {
    body: string; tone: NoticeTone; topic: NoticeTopic; sittingId: string | null; venueId: string | null;
    startsAt: string | null; minutes: number | null; push: boolean; remind: boolean;
  }) =>
    rpc<CommitteeNotice>('committee_announce', {
      p_body: n.body, p_tone: n.tone, p_topic: n.topic, p_sitting_id: n.sittingId, p_venue_id: n.venueId,
      p_starts_at: n.startsAt, p_minutes: n.minutes, p_push: n.push, p_remind: n.remind,
    }),
  removeNotice: (id: number) => rpc<CommitteeNotice>('committee_remove_notice', { p_id: id }),
  retryEmail: (id: number) => rpc<void>('committee_retry_email', { p_id: id }),

  /** `code` is the scanned QR text (one10:flat:…, one10:pass:… or one10:seat:…). */
  servePreview: (code: string, sittingId: string) =>
    rpc<ServePreview>('serve_preview', { p_code: code, p_sitting_id: sittingId }),
  servePass: (code: string, sittingId: string, lines: ServeLine[], counter: string, requestKey: string) =>
    rpc<{ ok: boolean; duplicate: boolean; via: Via; pass: Pass; seat: SeatInfo | null }>('serve_pass', {
      p_code: code, p_sitting_id: sittingId, p_lines: lines, p_counter: counter, p_request_key: requestKey,
    }),
  undoServe: (requestKey: string) => rpc<{ ok: boolean; pass: Pass }>('undo_serve', { p_request_key: requestKey }),
  findPasses: (q: string, sittingId: string) => rpc<FoundPass[]>('find_passes', { p_q: q, p_sitting_id: sittingId }),

  people: (q: string) => rpc<Person[]>('admin_people', { p_q: q.trim() || null }),
  setRole: (email: string, role: Role, on: boolean) => rpc<Role[]>('admin_set_role', { p_email: email, p_role: role, p_on: on }),
  updateSitting: (id: string, opensAt: string, closesAt: string, grace: number, bookingClosesAt: string) =>
    rpc<Sitting>('admin_update_sitting', {
      p_id: id, p_opens_at: opensAt, p_closes_at: closesAt, p_grace_minutes: grace, p_booking_closes_at: bookingClosesAt,
    }),
  extendSitting: (id: string, minutes: number) => rpc<Sitting>('admin_extend_sitting', { p_id: id, p_minutes: minutes }),
  adminEvents: () => rpc<AdminEvent[]>('admin_events'),
  saveEvent: (id: string | null, name: string, nameBn: string, bookingOpen: boolean, bhogCap: number | null, code?: string) =>
    rpc<AdminEvent>('admin_save_event', { p_id: id, p_name: name, p_name_bn: nameBn, p_booking_open: bookingOpen, p_bhog_cap: bhogCap, p_code: code || null }),
  setEventStatus: (id: string, status: EventStatus) => rpc<AdminEvent>('admin_set_event_status', { p_id: id, p_status: status }),
  copyEvent: (from: string, name: string, nameBn: string, firstDate: string) =>
    rpc<AdminEvent>('admin_copy_event', { p_from: from, p_name: name, p_name_bn: nameBn, p_first_date: firstDate }),
  saveSitting: (eventId: string, id: string | null, s: { dayLabel: string; dayLabelBn: string; meal: Meal; label: string; opensAt: string; closesAt: string; grace: number; bookingClosesAt: string }) =>
    rpc<Sitting>('admin_save_sitting', {
      p_event: eventId, p_id: id, p_day_label: s.dayLabel, p_day_label_bn: s.dayLabelBn, p_meal: s.meal, p_label: s.label,
      p_opens_at: s.opensAt, p_closes_at: s.closesAt, p_grace_minutes: s.grace, p_booking_closes_at: s.bookingClosesAt,
    }),
  deleteSitting: (id: string) => rpc<null>('admin_delete_sitting', { p_id: id }),
  saveMenuItem: (sittingId: string, kind: Kind, items: string[], adultPrice: number, childPrice: number, isFree: boolean) =>
    rpc<MenuItem>('admin_save_menu_item', { p_sitting: sittingId, p_kind: kind, p_items: items, p_adult_price: adultPrice, p_child_price: childPrice, p_is_free: isFree }),
  deleteMenuItem: (id: string) => rpc<null>('admin_delete_menu_item', { p_id: id }),
  saveVenue: (id: string | null, name: string, nameBn: string, where: string, notes: string) =>
    rpc<Venue>('admin_save_venue', { p_id: id, p_name: name, p_name_bn: nameBn, p_where: where, p_notes: notes }),
  setMealVenue: (menuItemId: string, venueId: string) => rpc<unknown>('admin_set_meal_venue', { p_menu_item_id: menuItemId, p_venue_id: venueId }),
  setSetting: (key: keyof Settings, value: boolean | number | string | null) => rpc<unknown>('admin_set_setting', { p_key: key, p_value: value }),
  audit: (limit = 200) => rpc<AuditRow[]>('admin_audit', { p_limit: limit }),
  exportOrders: () => rpc<ExportOrder[]>('export_orders'),
  exportServes: () => rpc<ServeExport[]>('export_serves'),
};
